import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import config from "./config";

type ProductDto = {
  id: number;
  name: string;
  imageUrl: string;
  salePrice: number;
  lastUpdated: string;
  description?: string;
  brand?: string;
  model?: string;
  retailer?: string;
};

type NotifyListResultDTO = {
  notificationId: number;
  notificationUpdatedDate: string;
  product: ProductDto;
  enabled?: boolean;
};

const NotifyList: React.FC = () => {
  const { getAccessTokenSilently, user, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const [notifyList, setNotifyList] = useState<NotifyListResultDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchNotifyList = async () => {
      if (!user) {
        loginWithRedirect();
        return;
      }
      try {
        const token = await getAccessTokenSilently();
        const res = await fetch(`${config.apiBaseUrl}/api/notifylist`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error("Failed to fetch notify list");
        const data = await res.json();
        setNotifyList(data);
        setSelectedIndex(0); // Show first row details on load
      } catch {
        setNotifyList([]);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifyList();
    // eslint-disable-next-line
  }, [user]);

  const handleToggle = async (notificationId: number, checked: boolean) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${config.apiBaseUrl}/api/notifylist/${notificationId}/toggle`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled: checked }),
      });
      setNotifyList(list =>
        list.map(item =>
          item.notificationId === notificationId ? { ...item, enabled: checked } : item
        )
      );
    } catch {
      alert("Failed to update notification status.");
    }
  };

  const handleDelete = async (notificationId: number) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${config.apiBaseUrl}/api/notifylist/${notificationId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setNotifyList(list => list.filter(item => item.notificationId !== notificationId));
      // If deleted row was selected, select first row or none
      setSelectedIndex(prev =>
        prev >= notifyList.length - 1 ? 0 : prev
      );
    } catch {
      alert("Failed to delete notification.");
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        fontSize: '1.2rem',
        color: '#666',
        padding: '40px 20px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px'
        }}>
          {/* Loading Spinner */}
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #2c5aa0',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          
          {/* Loading Text */}
          <div style={{
            fontSize: '1.1rem',
            fontWeight: '500'
          }}>
            Loading Notify List...
          </div>
          
          {/* Animated Dots */}
          <div style={{
            fontSize: '1.5rem',
            letterSpacing: '2px',
            animation: 'dots 1.5s infinite'
          }}>
            • • •
          </div>
        </div>
        
        {/* CSS Animation Styles */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes dots {
            0%, 20% { opacity: 0; }
            50% { opacity: 1; }
            100% { opacity: 0; }
          }
        `}</style>
      </div>
    );
  }

  const selectedItem = notifyList[selectedIndex];

  return (
    <div style={{ 
      maxWidth: 1200, 
      margin: "0 auto", 
      padding: isMobile ? 12 : 24, 
      display: "flex", 
      flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? 16 : 32 
    }}>
      <div style={{ flex: isMobile ? "1" : "2" }}>
        <button
          onClick={() => navigate('/')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            marginBottom: '24px',
            backgroundColor: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#1d4ed8'}
          onMouseOut={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#2563eb'}
        >
          ← Back to Home
        </button>
        <h2 style={{ textAlign: 'left', fontWeight: 'bold', fontSize: isMobile ? '1.5rem' : '2rem', marginBottom: isMobile ? '16px' : '24px' }}>Notify List</h2>
        {notifyList.length === 0 ? (
          <p style={{ textAlign: 'left' }}>Your notification list is empty.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
              <thead>
                <tr style={{ background: "#f3f4f6" }}>
                  <th style={{ padding: 8, border: "1px solid #ddd", textAlign: 'left' }}>Image</th>
                  <th style={{ padding: 8, border: "1px solid #ddd", textAlign: 'left' }}>Product Name</th>
                  <th style={{ padding: 8, border: "1px solid #ddd", textAlign: 'left' }}>Price</th>
                  <th style={{ padding: 8, border: "1px solid #ddd", textAlign: 'left' }}>Last Updated</th>
                  <th style={{ padding: 8, border: "1px solid #ddd", textAlign: 'left' }}>Enabled</th>
                  <th style={{ padding: 8, border: "1px solid #ddd", textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
            <tbody>
              {notifyList.map((item, idx) => (
                <tr
                  key={item.notificationId}
                  style={{
                    background: idx === selectedIndex ? "#e0e7ff" : undefined,
                    cursor: "pointer"
                  }}
                  onClick={() => setSelectedIndex(idx)}
                >
                  <td style={{ padding: 8, border: "1px solid #ddd", textAlign: "left" }}>
                    <img
                      src={item.product.imageUrl}
                      alt={item.product.name}
                      style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 4 }}
                    />
                  </td>
                  <td style={{ padding: 8, border: "1px solid #ddd", textAlign: "left" }}>{item.product.name}</td>
                  <td style={{ padding: 8, border: "1px solid #ddd", textAlign: "left" }}>
                    ${item.product.salePrice?.toFixed(2)}
                  </td>
                  <td style={{ padding: 8, border: "1px solid #ddd", textAlign: "left" }}>
                    {new Date(item.notificationUpdatedDate).toLocaleString()}
                  </td>
                  <td style={{ padding: 8, border: "1px solid #ddd", textAlign: "left" }}>
                    <input
                      type="checkbox"
                      checked={item.enabled ?? true}
                      onChange={e => handleToggle(item.notificationId, e.target.checked)}
                      onClick={e => e.stopPropagation()}
                    />
                  </td>
                  <td style={{ padding: 8, border: "1px solid #ddd", textAlign: "left" }}>
                    <button
                      style={{
                        background: "#ef4444",
                        color: "#fff",
                        border: "none",
                        borderRadius: 4,
                        padding: "6px 12px",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        handleDelete(item.notificationId);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      <div style={{
        flex: isMobile ? "1" : "1",
        background: "#f9fafb",
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        padding: isMobile ? 16 : 24,
        minHeight: 300
      }}>
        <h3 style={{ textAlign: 'left', fontWeight: 'bold', fontSize: '1.5rem', marginBottom: '20px' }}>Notification Details</h3>
        {selectedItem ? (
          <div style={{ textAlign: 'left' }}>
            <img
              src={selectedItem.product.imageUrl}
              alt={selectedItem.product.name}
              style={{
                width: 120,
                height: 120,
                objectFit: "cover",
                borderRadius: 8,
                marginBottom: 16,
                background: "#eee",
                display: 'block'
              }}
            />
            <h4 style={{ textAlign: 'left', fontWeight: 'bold', fontSize: '1.3rem', color: '#2c5aa0', marginBottom: '20px' }}>{selectedItem.product.name}</h4>
            <p style={{ textAlign: 'left' }}><strong>Price:</strong> ${selectedItem.product.salePrice?.toFixed(2)}</p>
            <p style={{ textAlign: 'left' }}><strong>Last Updated:</strong> {new Date(selectedItem.notificationUpdatedDate).toLocaleString()}</p>
            <p style={{ textAlign: 'left' }}><strong>Enabled:</strong> {selectedItem.enabled ? "Yes" : "No"}</p>
            <div style={{ textAlign: 'left', marginTop: '12px' }}>
              <strong>Description:</strong>
              <div 
                style={{ marginTop: '8px', lineHeight: '1.6' }}
                dangerouslySetInnerHTML={{ __html: selectedItem.product.description || '' }}
              />
            </div>
            <p style={{ textAlign: 'left' }}><strong>Brand:</strong> {selectedItem.product.brand}</p>
            <p style={{ textAlign: 'left' }}><strong>Model:</strong> {selectedItem.product.model}</p>
            <p style={{ textAlign: 'left' }}><strong>Retailer:</strong> {selectedItem.product.retailer}</p>
            <button
              onClick={() => navigate(`/product/${selectedItem.product.id}`)}
              style={{
                marginTop: '24px',
                padding: '12px 24px',
                backgroundColor: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                width: '100%'
              }}
              onMouseOver={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#1d4ed8'}
              onMouseOut={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#2563eb'}
            >
              View Full Details
            </button>
          </div>
        ) : (
          <p style={{ textAlign: 'left' }}>Select a notification to see the details</p>
        )}
      </div>
    </div>
  );
};

export default NotifyList;